import axios from "axios";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { FiActivity, FiRefreshCw } from "react-icons/fi";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const getMetricUnit = (path) => {
  const field = path[path.length - 1].toLowerCase();
  if (field.includes("volt")) return "V";
  if (field === "amp" || field.includes("current")) return "A";
  if (field === "soc" || field.includes("percent")) return "%";
  if (field.includes("temp")) return "°C";
  if (field.includes("capacity")) return "Ah";
  return "";
};

const getMetricLabel = (path) => {
  const [frame, ...fields] = path;
  const fieldLabel = fields
    .map((field, index) =>
      /^\d+$/.test(field)
        ? `Cell ${Number(field) + 1}`
        : field.replace(/([a-z])([A-Z])/g, "$1 $2")
    )
    .join(" - ");
  return `${frame} - ${fieldLabel}`;
};

const getNumericMetrics = (value, path = []) => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) =>
      getNumericMetrics(item, [...path, String(index)])
    );
  }

  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([key, item]) =>
      getNumericMetrics(item, [...path, key])
    );
  }

  const numericValue = value === "" || value == null ? NaN : Number(value);
  if (!Number.isFinite(numericValue)) return [];

  const key = path.join(".");
  return [{ key, path, label: getMetricLabel(path), unit: getMetricUnit(path) }];
};

const timeRanges = [
  { key: "5m", label: "5 min", duration: 5 * 60 * 1000 },
  { key: "15m", label: "15 min", duration: 15 * 60 * 1000 },
  { key: "30m", label: "30 min", duration: 30 * 60 * 1000 },
  { key: "3h", label: "3 hours", duration: 3 * 60 * 60 * 1000 },
  { key: "1d", label: "1 day", duration: 24 * 60 * 60 * 1000 },
  { key: "7d", label: "7 days", duration: 7 * 24 * 60 * 60 * 1000 },
];

const DeviceHistory = ({ deviceId }) => {
  const today = format(new Date(), "yyyy-MM-dd");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedRange, setSelectedRange] = useState("30m");
  const [historyRecords, setHistoryRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedMetricKey, setSelectedMetricKey] = useState("F0.volt");
  const requestIdRef = useRef(0);

  const loadHistory = async (start, end, rangeKey) => {
    if (!deviceId) return;

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setErrorMessage("");
    setSelectedRange(rangeKey);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}api/v1/bms/device-history/${deviceId}`,
        {
          params: {
            startTime: start.toISOString(),
            endTime: end.toISOString(),
          },
        }
      );

      if (requestId === requestIdRef.current) {
        setHistoryRecords(res?.data?.data || []);
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error("Unable to load device history:", error);
        setErrorMessage("History data could not be loaded. Please try again.");
        setHistoryRecords([]);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  const handleRangeChange = (range) => {
    const end = new Date();
    const start = new Date(end.getTime() - range.duration);
    loadHistory(start, end, range.key);
  };

  const handleCustomRange = () => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59.999`);

    if (start > end) {
      setErrorMessage("Start date must be before the end date.");
      return;
    }

    loadHistory(start, end, "custom");
  };

  useEffect(() => {
    if (deviceId) handleRangeChange(timeRanges[2]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const availableMetrics = Array.from(
    new Map(
      historyRecords.flatMap((record) => getNumericMetrics(record?.bmsData))
        .map((metric) => [metric.key, metric])
    ).values()
  );

  const selectedMetric =
    availableMetrics.find(({ key }) => key === selectedMetricKey) ||
    availableMetrics[0];

  const chartData = historyRecords
    .map((record) => {
      const values = Object.fromEntries(
        getNumericMetrics(record?.bmsData).map(({ key, path }) => {
          const value = path.reduce((current, part) => current?.[part], record.bmsData);
          return [key, Number(value)];
        })
      );
      return {
        timestamp: new Date(record?.createdAt || record?.time).getTime(),
        selectedValue: selectedMetric ? values[selectedMetric.key] : undefined,
      };
    })
    .filter((record) => Number.isFinite(record.timestamp));

  const lastRecord = historyRecords[historyRecords.length - 1];
  const selectedRangeLabel =
    timeRanges.find(({ key }) => key === selectedRange)?.label || "Custom range";
  const formatChartTime = (timestamp) =>
    new Date(timestamp).toLocaleString([], {
      month: selectedRange === "7d" ? "short" : undefined,
      day: selectedRange === "7d" ? "numeric" : undefined,
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-4 py-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 text-slate-900">
              <FiActivity className="text-emerald-700" aria-hidden="true" />
              <h2 className="text-base font-semibold">Battery history</h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {selectedRangeLabel} · {chartData.length} plotted points
            </p>
          </div>

          <div className="flex overflow-x-auto border border-slate-200 rounded-md bg-slate-50 p-1" role="group" aria-label="History range">
            {timeRanges.map((range) => (
              <button
                key={range.key}
                type="button"
                onClick={() => handleRangeChange(range)}
                className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap rounded transition-colors ${
                  selectedRange === range.key
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white hover:text-slate-900"
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 px-4 py-3 bg-slate-50">
          <label className="text-xs font-medium text-slate-600">
            <span className="block mb-1">Start date</span>
            <input
              type="date"
              className="input input-bordered input-sm bg-white"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <label className="text-xs font-medium text-slate-600">
            <span className="block mb-1">End date</span>
            <input
              type="date"
              className="input input-bordered input-sm bg-white"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </label>
          <button
            type="button"
            onClick={handleCustomRange}
            className="btn btn-sm bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
            disabled={isLoading}
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
            Apply dates
          </button>
        </div>
      </div>

      {lastRecord && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="border-l-4 border-emerald-700 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs text-slate-500">Latest voltage</p>
            <p className="text-xl font-semibold text-slate-900">{lastRecord?.bmsData?.F0?.volt} <span className="text-xs font-normal text-slate-500">V</span></p>
          </div>
          <div className="border-l-4 border-amber-700 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs text-slate-500">Latest current</p>
            <p className="text-xl font-semibold text-slate-900">{lastRecord?.bmsData?.F0?.amp} <span className="text-xs font-normal text-slate-500">A</span></p>
          </div>
          <div className="border-l-4 border-rose-700 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs text-slate-500">Latest SOC</p>
            <p className="text-xl font-semibold text-slate-900">{lastRecord?.bmsData?.F0?.soc} <span className="text-xs font-normal text-slate-500">%</span></p>
          </div>
          <div className="border-l-4 border-slate-400 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs text-slate-500">Last received</p>
            <p className="text-sm font-semibold text-slate-900 mt-1">
              {lastRecord?.createdAt
                ? new Date(lastRecord.createdAt).toLocaleString()
                : "--"}
            </p>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg bg-white p-4">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="text-xs font-medium text-slate-600">
            <span className="block mb-1">History value</span>
            <select
              value={selectedMetric?.key || ""}
              onChange={(event) => setSelectedMetricKey(event.target.value)}
              className="select select-bordered select-sm bg-white min-w-64"
              disabled={availableMetrics.length === 0}
            >
              {availableMetrics.length === 0 && <option value="">No values available</option>}
              {availableMetrics.map(({ key, label, unit }) => (
                <option key={key} value={key}>
                  {label}{unit ? ` (${unit})` : ""}
                </option>
              ))}
            </select>
          </label>
          {selectedMetric && (
            <p className="text-xs text-slate-500 pb-2">
              {chartData.filter(({ selectedValue }) => Number.isFinite(selectedValue)).length} readings
            </p>
          )}
        </div>

        {errorMessage && (
          <div className="mb-3 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
            {errorMessage}
          </div>
        )}

        <div className="h-80 md:h-96 min-w-0">
          {chartData.length > 0 && selectedMetric ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={formatChartTime}
                  minTickGap={40}
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={{ stroke: "#cbd5e1" }}
                  tickLine={false}
                />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleString()}
                  formatter={(value) => [`${value} ${selectedMetric.unit}`, selectedMetric.label]}
                  contentStyle={{ border: "1px solid #cbd5e1", borderRadius: 6, boxShadow: "0 8px 20px rgba(15, 23, 42, 0.1)" }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                <Line
                  type="linear"
                  dataKey="selectedValue"
                  name={selectedMetric.label}
                  stroke="#047857"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-500">
              <FiActivity className="text-3xl text-slate-300 mb-2" aria-hidden="true" />
              <p className="text-sm font-medium text-slate-700">
                {isLoading
                  ? "Loading history"
                  : "No readings in this range"}
              </p>
              <p className="text-xs mt-1">
                {isLoading ? "Fetching device readings..." : "Try a longer time range."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceHistory;
